import { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { db } from './firebase';
import { doc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

const SUITS = ['♠', '♥', '♦', '♣'];
const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const POINTS = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 10, Q: 10, K: 0 };
const DISCONNECT_TIMEOUT = 30000;

function buildDeck() {
  let d = [];
  for (let s of SUITS)
    for (let v of VALUES)
      d.push({ suit: s, value: v, points: POINTS[v], id: Math.random().toString(36).slice(2) });
  for (let i = d.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

function calcScore(cards) {
  const counts = {};
  cards.forEach(c => { counts[c.value] = (counts[c.value] || 0) + 1; });
  let total = 0;
  const processed = {};
  cards.forEach(card => {
    if (!processed[card.value]) {
      processed[card.value] = true;
      total += (counts[card.value] % 2) * card.points;
    }
  });
  return total;
}

function getCardCode(card) {
  const valueMap = { 'A': 'A', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '10': '0', 'J': 'J', 'Q': 'Q', 'K': 'K' };
  const suitMap = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' };
  return `${valueMap[card.value]}${suitMap[card.suit]}`;
}

const CARD_BACK_STYLES = {
  classic:  { background: '#1D4E89' },
  neon:     { background: 'linear-gradient(135deg,#7F77DD,#D85A30)' },
  midnight: { background: '#1a1a2e' },
  vintage:  { background: '#8B4513' },
  luxury:   { background: 'linear-gradient(135deg,#B8860B,#FFD700)' },
  ocean:    { background: 'linear-gradient(135deg,#006994,#00CED1)' },
};

const TABLE_STYLES = {
  felt:          { background: '#1D6B3A' },
  'green-felt':  { background: '#1D6B3A' },
  'tb-midnight': { background: '#1a1a2e' },
  'tb-beach':    { background: '#F4A460' },
  'tb-wood':     { background: '#8B4513' },
  'tb-marble':   { background: '#E8E8E8' },
  'tb-neon':     { background: '#0D0D0D' },
};

const S = {
  btn: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  btnSm: { background: 'none', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: '#fff', cursor: 'pointer' },
  btnWhite: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#fff', cursor: 'pointer' },
};

// Responsive card size based on screen width
function getCardSize() {
  const w = window.innerWidth;
  if (w < 360) return { w: 42, h: 60 };
  if (w < 420) return { w: 48, h: 68 };
  return { w: 56, h: 80 };
}

function CardBack({ skinId = 'classic', glow = false, onClick, size }) {
  const style = CARD_BACK_STYLES[skinId] || CARD_BACK_STYLES.classic;
  return (
    <div onClick={onClick} style={{
      width: size.w, height: size.h, borderRadius: 5,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, color: 'rgba(255,255,255,0.3)',
      cursor: onClick ? 'pointer' : 'default',
      outline: glow ? '3px solid #1D9E75' : 'none', outlineOffset: 2,
      flexShrink: 0, ...style
    }}>♦</div>
  );
}

function CardFace({ card, highlight = false, onClick, size }) {
  const code = getCardCode(card);
  return (
    <div onClick={onClick} style={{
      width: size.w, height: size.h, borderRadius: 5,
      border: highlight ? '2px solid #1D9E75' : '1px solid #e0e0e0',
      cursor: onClick ? 'pointer' : 'default',
      overflow: 'hidden',
      boxShadow: highlight ? '0 0 0 2px #1D9E75' : 'none',
      background: '#fff', flexShrink: 0,
    }}>
      <img
        src={`https://deckofcardsapi.com/static/img/${code}.png`}
        alt={`${card.value}${card.suit}`}
        style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
    </div>
  );
}

export default function OnlineGame({ gameId, onLeave }) {
  const { user, profile } = useAuth();
  const skinId = profile?.cosmetics?.activeSkin || 'classic';
  const tableId = profile?.cosmetics?.activeTable || 'felt';
  const tableStyle = TABLE_STYLES[tableId] || TABLE_STYLES.felt;
  const [cardSize, setCardSize] = useState(getCardSize());

  const [gameState, setGameState] = useState(null);
  const [peekingCards, setPeekingCards] = useState(new Set());
  const gameRef = doc(db, 'games', gameId);
  const botTimer = useRef(null);
  const heartbeatTimer = useRef(null);

  // Update card size on resize
  useEffect(() => {
    const handleResize = () => setCardSize(getCardSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const myIndex = gameState?.players?.findIndex(p => p.uid === user?.uid) ?? -1;
  const isMyTurn = gameState?.currentPlayer === myIndex;
  const me = gameState?.players?.[myIndex];
  const currentPlayerData = gameState?.players?.[gameState?.currentPlayer];

  useEffect(() => {
    if (!gameId || !user) return;
    const sendHeartbeat = () => updateDoc(gameRef, { [`playerPresence.${user.uid}`]: serverTimestamp() }).catch(() => {});
    sendHeartbeat();
    heartbeatTimer.current = setInterval(sendHeartbeat, 10000);
    return () => clearInterval(heartbeatTimer.current);
  }, [gameId, user]);

  useEffect(() => {
    if (!gameId) return;
    const unsub = onSnapshot(gameRef, snap => { if (snap.exists()) setGameState(snap.data()); });
    return () => unsub();
  }, [gameId]);

  useEffect(() => {
    if (!gameState || gameState.status !== 'playing') return;
    if (botTimer.current) clearTimeout(botTimer.current);
    const cp = gameState.players[gameState.currentPlayer];
    if (!cp || cp.uid === user?.uid) return;
    const presence = gameState.playerPresence?.[cp.uid];
    const lastSeen = presence?.toMillis?.() || 0;
    const elapsed = Date.now() - lastSeen;
    if (elapsed > DISCONNECT_TIMEOUT) runBotTurn(gameState);
    else botTimer.current = setTimeout(() => runBotTurn(gameState), DISCONNECT_TIMEOUT - elapsed);
    return () => { if (botTimer.current) clearTimeout(botTimer.current); };
  }, [gameState?.currentPlayer, gameState?.turnPhase]);

  const runBotTurn = async (state) => {
    if (!state || state.status !== 'playing') return;
    const cp = state.players[state.currentPlayer];
    if (!cp) return;
    const { players, deck, discard, currentPlayer, turnPhase, drawnCard, maxScore } = state;
    if (turnPhase === 'draw') {
      const topDiscard = discard[discard.length - 1];
      if (topDiscard && topDiscard.points <= 3) {
        const nd = [...discard]; nd.pop();
        await updateDoc(gameRef, { discard: nd, drawnCard: topDiscard, turnPhase: 'swap', lastAction: serverTimestamp() });
      } else if (deck.length > 0) {
        const nd = [...deck]; const card = nd.shift();
        await updateDoc(gameRef, { deck: nd, drawnCard: card, turnPhase: 'action', lastAction: serverTimestamp() });
      }
      return;
    }
    if (turnPhase === 'action' && drawnCard) {
      const fd = cp.cards.map((c, i) => ({ ...c, idx: i })).filter(c => !c.faceUp);
      if (fd.length > 0) {
        const t = fd[Math.floor(Math.random() * fd.length)];
        const np = players.map((pl, pli) => pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === t.idx ? { ...c, faceUp: true } : c) });
        await advanceTurn(np, deck, [...discard, drawnCard], maxScore);
      } else await advanceTurn(players, deck, [...discard, drawnCard], maxScore);
      return;
    }
    if (turnPhase === 'swap' && drawnCard) {
      const fd = cp.cards.map((c, i) => ({ ...c, idx: i })).filter(c => !c.faceUp);
      if (fd.length > 0) {
        const t = fd[Math.floor(Math.random() * fd.length)];
        const np = players.map((pl, pli) => pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === t.idx ? { ...drawnCard, faceUp: true, position: cii } : c) });
        await advanceTurn(np, deck, [...discard, cp.cards[t.idx]], maxScore);
      } else await advanceTurn(players, deck, [...discard, drawnCard], maxScore);
      return;
    }
    if (turnPhase === 'flip') {
      const fd = cp.cards.map((c, i) => ({ ...c, idx: i })).filter(c => !c.faceUp);
      if (fd.length > 0) {
        const t = fd[Math.floor(Math.random() * fd.length)];
        const np = players.map((pl, pli) => pli !== currentPlayer ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === t.idx ? { ...c, faceUp: true } : c) });
        await advanceTurn(np, deck, discard, maxScore);
      } else await advanceTurn(players, deck, discard, maxScore);
    }
  };

  const advanceTurn = async (newPlayers, newDeck, newDiscard, maxSc) => {
    const allFaceUp = newPlayers.every(p => p.cards.every(c => c.faceUp));
    if (allFaceUp) {
      const scores = newPlayers.map(p => ({ name: p.name, roundScore: calcScore(p.cards) }));
      const min = Math.min(...scores.map(s => s.roundScore));
      const updated = newPlayers.map((p, i) => ({ ...p, score: p.score + scores[i].roundScore }));
      const isGameOver = updated.some(p => p.score >= maxSc);
      await updateDoc(gameRef, {
        players: updated, deck: newDeck, discard: newDiscard, drawnCard: null, turnPhase: 'draw',
        status: isGameOver ? 'finished' : 'roundover',
        roundResults: scores.map((s, i) => ({ ...s, newTotal: updated[i].score, isWinner: s.roundScore === min, eliminated: updated[i].score >= maxSc })),
        lastAction: serverTimestamp(),
      });
      return;
    }
    const currentIdx = gameState.currentPlayer;
    const total = newPlayers.length;
    let nextPlayer = (currentIdx + 1) % total;
    for (let i = 1; i < total; i++) {
      const idx = (currentIdx + i) % total;
      if (newPlayers[idx].cards.some(c => !c.faceUp)) { nextPlayer = idx; break; }
    }
    await updateDoc(gameRef, { players: newPlayers, deck: newDeck, discard: newDiscard, drawnCard: null, currentPlayer: nextPlayer, turnPhase: 'draw', lastAction: serverTimestamp() });
  };

  const drawFromDeck = async () => {
    if (!isMyTurn || gameState.turnPhase !== 'draw' || gameState.deck.length === 0) return;
    const nd = [...gameState.deck]; const card = nd.shift();
    await updateDoc(gameRef, { deck: nd, drawnCard: card, turnPhase: 'action', lastAction: serverTimestamp() });
  };

  const takeDiscard = async () => {
    if (!isMyTurn || gameState.turnPhase !== 'draw' || gameState.discard.length === 0) return;
    const nd = [...gameState.discard]; const card = nd.pop();
    await updateDoc(gameRef, { discard: nd, drawnCard: card, turnPhase: 'swap', lastAction: serverTimestamp() });
  };

  const flipCardDiscardDrawn = async (ci) => {
    if (!isMyTurn || gameState.turnPhase !== 'action' || !gameState.drawnCard) return;
    if (me.cards[ci].faceUp) return;
    const np = gameState.players.map((pl, pli) => pli !== myIndex ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...c, faceUp: true } : c) });
    await advanceTurn(np, gameState.deck, [...gameState.discard, gameState.drawnCard], gameState.maxScore);
  };

  const swapDrawnIntoCard = async (ci) => {
    if (!isMyTurn || gameState.turnPhase !== 'swap' || !gameState.drawnCard) return;
    if (me.cards[ci].faceUp) return;
    const old = me.cards[ci];
    const np = gameState.players.map((pl, pli) => pli !== myIndex ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...gameState.drawnCard, faceUp: true, position: cii } : c) });
    await advanceTurn(np, gameState.deck, [...gameState.discard, old], gameState.maxScore);
  };

  const flipBlindCard = async (ci) => {
    if (!isMyTurn || gameState.turnPhase !== 'flip') return;
    if (me.cards[ci].faceUp) return;
    const np = gameState.players.map((pl, pli) => pli !== myIndex ? pl : { ...pl, cards: pl.cards.map((c, cii) => cii === ci ? { ...c, faceUp: true } : c) });
    await advanceTurn(np, gameState.deck, gameState.discard, gameState.maxScore);
  };

  const discardDrawn = async () => {
    if (!gameState.drawnCard) return;
    const hasFaceDown = me.cards.some(c => !c.faceUp);
    const nd = [...gameState.discard, gameState.drawnCard];
    if (hasFaceDown) {
      await updateDoc(gameRef, { discard: nd, drawnCard: null, turnPhase: 'flip', lastAction: serverTimestamp() });
    } else {
      await advanceTurn(gameState.players, gameState.deck, nd, gameState.maxScore);
    }
  };

  const startNextRound = async () => {
    if (gameState.players.some(p => p.score >= gameState.maxScore)) {
      await updateDoc(gameRef, { status: 'finished', lastAction: serverTimestamp() });
      return;
    }
    const freshDeck = buildDeck();
    const np = gameState.players.map(p => ({ ...p, cards: Array.from({ length: 4 }, (_, i) => ({ ...freshDeck.shift(), faceUp: false, position: i })) }));
    await updateDoc(gameRef, { players: np, deck: freshDeck, discard: [], drawnCard: null, currentPlayer: 0, turnPhase: 'draw', round: (gameState.round || 1) + 1, status: 'playing', roundResults: null, lastAction: serverTimestamp() });
  };

  const setPeek = (key, val) => setPeekingCards(prev => { const n = new Set(prev); val ? n.add(key) : n.delete(key); return n; });

  if (!gameState) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div><div>Loading game...</div></div>
    </div>
  );

  if (gameState.status === 'roundover' && gameState.roundResults) {
    const anyElim = gameState.roundResults.some(r => r.eliminated);
    const isHost = gameState.hostId === user?.uid;
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', padding: '1rem' }}>
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '1.5rem', maxWidth: 480, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Round {gameState.round} results</div>
          {[...gameState.roundResults].sort((a, b) => a.roundScore - b.roundScore).map(r => {
            const pd = gameState.players.find(p => p.name === r.name);
            const counts = {}; pd?.cards.forEach(c => { counts[c.value] = (counts[c.value] || 0) + 1; });
            return (
              <div key={r.name} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: r.isWinner ? '#0F6E56' : 'inherit' }}>
                    {r.isWinner ? '🏅 ' : ''}{r.name}{pd?.uid === user?.uid ? ' (you)' : ''}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: '#888', fontSize: 13 }}>+{r.roundScore} pts</span>
                    <strong>{r.newTotal} total</strong>
                    {r.eliminated && <span style={{ fontSize: 11, background: '#FCEBEB', color: '#A32D2D', padding: '2px 6px', borderRadius: 6 }}>out</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {pd?.cards.map((card, ci) => {
                    const cancelled = counts[card.value] === 2 || counts[card.value] === 4;
                    return (
                      <div key={ci} style={{ width: 36, height: 50, borderRadius: 4, background: '#fff', border: cancelled ? '2px solid #1D9E75' : '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: ['♥','♦'].includes(card.suit) ? '#D85A30' : '#1a1a1a', position: 'relative', opacity: cancelled ? 0.6 : 1 }}>
                        <div>{card.value}</div><div style={{ fontSize: '0.7em' }}>{card.suit}</div>
                        {cancelled && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#1D9E75', fontWeight: 700 }}>✕</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {isHost
            ? <button onClick={startNextRound} style={{ ...S.btn, width: '100%', marginTop: 16 }}>{anyElim ? 'See final results' : 'Deal next round'}</button>
            : <div style={{ textAlign: 'center', padding: '1rem', color: '#888', fontSize: 14, marginTop: 12 }}>⏳ Waiting for host...</div>
          }
        </div>
      </div>
    );
  }

  if (gameState.status === 'finished') {
    const sorted = [...gameState.players].sort((a, b) => a.score - b.score);
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', padding: '1rem' }}>
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '1.5rem', maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Game over!</div>
          <div style={{ fontSize: 16, color: '#0F6E56', marginBottom: 20 }}>{sorted[0].name} wins!</div>
          {sorted.map((p, i) => (
            <div key={p.uid} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
              <span>{['🥇','🥈','🥉'][i] || ''} {p.name}{p.uid === user?.uid ? ' (you)' : ''}</span>
              <strong>{p.score} pts</strong>
            </div>
          ))}
          <button onClick={onLeave} style={{ ...S.btn, width: '100%', marginTop: 20 }}>Back to menu</button>
        </div>
      </div>
    );
  }

  const discardTop = (gameState.turnPhase === 'swap' || gameState.turnPhase === 'action') ? null : gameState.discard?.[gameState.discard.length - 1];
  const numPlayers = gameState.players.length;
  const cols = numPlayers <= 2 ? 2 : numPlayers <= 4 ? 2 : numPlayers <= 6 ? 3 : 4;

  return (
    <div style={{ minHeight: '100vh', padding: '0.4rem', fontFamily: 'sans-serif', boxSizing: 'border-box', ...tableStyle }}>
   <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', letterSpacing: 0.5 }}>Four Play</div>
          <button onClick={onLeave} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 12, color: '#fff', cursor: 'pointer' }}>
            Leave game
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
          Online • Round {gameState.round || 1} • Room {gameState.code}
        </div>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 6, padding: '5px 10px', marginBottom: 8, fontSize: 11, color: '#fff', textAlign: 'center' }}>
        {isMyTurn && gameState.turnPhase === 'draw' && <span><strong>Your turn</strong> — draw or take discard</span>}
        {isMyTurn && gameState.turnPhase === 'action' && <span><strong>Your turn</strong> — tap a face-down card</span>}
        {isMyTurn && gameState.turnPhase === 'swap' && <span><strong>Your turn</strong> — tap a face-down card to replace</span>}
        {isMyTurn && gameState.turnPhase === 'flip' && <span><strong>Your turn</strong> — tap a face-down card to flip</span>}
        {!isMyTurn && <span>⏳ <strong>{currentPlayerData?.name}</strong>'s turn...</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginBottom: 2, textTransform: 'uppercase' }}>Draw</div>
          <CardBack size={cardSize} skinId={skinId} onClick={isMyTurn && gameState.turnPhase === 'draw' ? drawFromDeck : undefined} glow={isMyTurn && gameState.turnPhase === 'draw'} />
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{gameState.deck?.length || 0}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginBottom: 2, textTransform: 'uppercase' }}>Drawn</div>
          {gameState.drawnCard
            ? <>
                <CardFace size={cardSize} card={gameState.drawnCard} highlight onClick={isMyTurn && gameState.turnPhase === 'action' ? async () => await updateDoc(gameRef, { turnPhase: 'swap' }) : undefined} />
                {isMyTurn && gameState.turnPhase === 'action' && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>tap to swap</div>}
              </>
            : <div style={{ width: cardSize.w, height: cardSize.h, borderRadius: 5, border: '1px dashed rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>—</span>
              </div>
          }
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginBottom: 2, textTransform: 'uppercase' }}>Discard</div>
          {discardTop
            ? <>
                <CardFace size={cardSize} card={discardTop} highlight={isMyTurn && gameState.turnPhase === 'draw'} onClick={isMyTurn && gameState.turnPhase === 'draw' ? takeDiscard : undefined} />
                {isMyTurn && gameState.turnPhase === 'draw' && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>tap to take</div>}
              </>
            : <div style={{ width: cardSize.w, height: cardSize.h, borderRadius: 5, border: '1px dashed rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>—</span>
              </div>
          }
        </div>
      </div>

      {isMyTurn && (gameState.turnPhase === 'action' || gameState.turnPhase === 'swap') && gameState.drawnCard && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <button onClick={discardDrawn} style={S.btnWhite}>
            {gameState.turnPhase === 'swap' ? 'Cancel — discard drawn' : 'Discard & flip one of mine'}
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cols, numPlayers)}, minmax(0,1fr))`, gap: 6 }}>
        {gameState.players.map((p, pi) => {
          const isMe = p.uid === user?.uid;
          const isActive = pi === gameState.currentPlayer;
          const isDisconnected = (() => {
            const presence = gameState.playerPresence?.[p.uid];
            const lastSeen = presence?.toMillis?.() || 0;
            return Date.now() - lastSeen > DISCONNECT_TIMEOUT;
          })();

          return (
            <div key={pi} style={{
              background: 'rgba(255,255,255,0.95)', borderRadius: 8, padding: 8,
              border: isActive ? '2px solid #1D9E75' : '2px solid transparent',
              boxShadow: isActive ? '0 0 0 2px rgba(29,158,117,0.2)' : 'none',
              opacity: isDisconnected ? 0.7 : 1, minWidth: 0,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
                  {p.name}{isMe ? ' (you)' : ''}
                </span>
                <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
                  {isActive && <span style={{ fontSize: 9, background: '#E1F5EE', color: '#0F6E56', padding: '1px 5px', borderRadius: 4 }}>{isMe ? 'you' : 'them'}</span>}
                  <span style={{ fontSize: 10, color: '#888', background: '#f5f5f5', padding: '1px 5px', borderRadius: 4 }}>{p.score}pt</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, width: 'fit-content', margin: '0 auto' }}>
                {p.cards.map((card, ci) => {
                  const isBottomTwo = ci >= 2;
                  const peeking = peekingCards.has(`${pi}-${ci}`);
                  const canFlipForDiscard = isMe && isActive && gameState.drawnCard && gameState.turnPhase === 'action' && !card.faceUp;
                  const canSwapIn = isMe && isActive && gameState.drawnCard && gameState.turnPhase === 'swap' && !card.faceUp;
                  const canFlipBlind = isMe && isActive && gameState.turnPhase === 'flip' && !card.faceUp;
                  const glow = canFlipForDiscard || canSwapIn || canFlipBlind;
                  const handleClick = canFlipForDiscard ? () => flipCardDiscardDrawn(ci)
                    : canSwapIn ? () => swapDrawnIntoCard(ci)
                    : canFlipBlind ? () => flipBlindCard(ci)
                    : undefined;

                  if (card.faceUp) return <CardFace key={ci} size={cardSize} card={card} />;
                  if (!isMe) return <CardBack key={ci} size={cardSize} skinId="classic" />;

                  if (isBottomTwo && isMe) {
                    return (
                      <div key={ci} style={{ position: 'relative', paddingBottom: 22 }}>
                        {peeking
                          ? <CardFace size={cardSize} card={card} onClick={handleClick} />
                          : <CardBack size={cardSize} skinId={skinId} glow={glow} onClick={handleClick} />
                        }
                        <button
                          onMouseDown={() => setPeek(`${pi}-${ci}`, true)}
                          onMouseUp={() => setPeek(`${pi}-${ci}`, false)}
                          onMouseLeave={() => setPeek(`${pi}-${ci}`, false)}
                          onTouchStart={e => { e.preventDefault(); setPeek(`${pi}-${ci}`, true); }}
                          onTouchEnd={() => setPeek(`${pi}-${ci}`, false)}
                          style={{ position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)', background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 3, fontSize: 8, padding: '1px 5px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          👁 peek
                        </button>
                      </div>
                    );
                  }
                  return <CardBack key={ci} size={cardSize} skinId={skinId} glow={glow} onClick={handleClick} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
